import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  Copy,
  Users,
  User,
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ChevronRight,
  ChevronDown,
  Lock,
  EyeOff,
  Eye,
  Unlock,
  Info,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Blocked reason descriptions
const BLOCKED_REASON_LABELS = {
  membership_inactive: 'Agency membership is not active',
  entitlement_missing: 'Not included in agency plan',
  plan_limit_restriction: 'Plan limit restriction',
  usage_limit_reached: 'Usage limit reached',
  seat_limit_restriction: 'Seat limit restriction',
  section_hidden: 'Hidden at agency level',
  section_locked: 'Locked at agency level',
  read_only_only: 'Only read-only allowed',
  processor_access_not_allowed: 'Payment processor not allowed',
  role_denied: 'Role does not permit this',
  role_template_ceiling_exceeded: 'Exceeds role template ceiling',
  user_already_has_permission: 'User already has this',
  invalid_target_user: 'Invalid target user',
  same_user: 'Cannot copy to same user',
};

// Access mode styles
const ACCESS_MODE_STYLES = {
  hidden: { bg: 'bg-slate-200', text: 'text-slate-600', icon: EyeOff },
  locked: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Lock },
  read_only: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Eye },
  full_access: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: Unlock },
};

/**
 * PermissionItem - Single permission display row
 */
const PermissionItem = ({ perm, status, reason, reasonCode }) => {
  const [expanded, setExpanded] = useState(false);
  const accessMode = perm.access_mode || 'full_access';
  const style = ACCESS_MODE_STYLES[accessMode] || ACCESS_MODE_STYLES.full_access;
  const Icon = style.icon;
  
  const statusColors = {
    add: 'text-emerald-600 bg-emerald-50',
    remove: 'text-red-600 bg-red-50',
    blocked: 'text-amber-600 bg-amber-50',
    skipped: 'text-slate-500 bg-slate-100',
    unchanged: 'text-slate-400 bg-slate-50',
  };
  
  return (
    <div 
      className={`p-3 rounded-lg border ${status === 'blocked' ? 'border-amber-200 bg-amber-50/50' : 'border-slate-200 bg-white'}`}
      data-testid={`permission-item-${perm.entitlement_key}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-1.5 rounded ${style.bg}`}>
            <Icon size={14} className={style.text} />
          </div>
          <div>
            <span className="text-sm font-medium text-stone-900 capitalize">
              {perm.entitlement_key?.replace(/_/g, ' ')}
            </span>
            {perm.access_mode_capped && (
              <Badge variant="outline" className="ml-2 text-xs">Capped</Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={statusColors[status] || statusColors.unchanged}>
            {status === 'add' && '+ Add'}
            {status === 'remove' && '- Remove'}
            {status === 'blocked' && 'Blocked'}
            {status === 'skipped' && 'Skipped'}
            {status === 'unchanged' && 'No Change'}
          </Badge>
          {(status === 'blocked' || status === 'skipped') && (
            <button 
              onClick={() => setExpanded(!expanded)}
              className="p-1 hover:bg-slate-100 rounded"
            >
              {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          )}
        </div>
      </div>
      
      {expanded && (status === 'blocked' || status === 'skipped') && (
        <div className="mt-2 ml-10 p-2 bg-slate-50 rounded text-sm">
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-500 mt-0.5" />
            <div>
              <span className="text-slate-700">
                {BLOCKED_REASON_LABELS[reasonCode] || reason || 'Unknown reason'}
              </span>
              {reasonCode && (
                <span className="ml-2 text-xs text-slate-400 font-mono">({reasonCode})</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * PreviewSummary - Summary cards for preview
 */
const PreviewSummary = ({ preview }) => {
  return (
    <div className="grid grid-cols-5 gap-3 mb-4" data-testid="preview-summary">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
        <p className="text-2xl font-bold text-emerald-700">{preview.total_to_add}</p>
        <p className="text-xs text-emerald-600">To Add</p>
      </div>
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
        <p className="text-2xl font-bold text-red-700">{preview.total_to_remove}</p>
        <p className="text-xs text-red-600">To Remove</p>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
        <p className="text-2xl font-bold text-amber-700">{preview.total_blocked}</p>
        <p className="text-xs text-amber-600">Blocked</p>
      </div>
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
        <p className="text-2xl font-bold text-slate-600">{preview.total_skipped}</p>
        <p className="text-xs text-slate-500">Skipped</p>
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
        <p className="text-2xl font-bold text-blue-700">{preview.permissions_unchanged?.length || 0}</p>
        <p className="text-xs text-blue-600">Unchanged</p>
      </div>
    </div>
  );
};

/**
 * CopyPermissionsModal - Single user permission copy
 */
const CopyPermissionsModal = ({
  isOpen,
  onClose,
  targetUser,
  agencyId,
  onSuccess,
}) => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [sourceUserId, setSourceUserId] = useState('');
  const [copyMode, setCopyMode] = useState('replace_all');
  const [copyScope, setCopyScope] = useState('permissions_only');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  
  const headers = { Authorization: `Bearer ${token}` };
  
  // Fetch users for source selection
  useEffect(() => {
    if (isOpen && agencyId) {
      fetchUsers();
    }
  }, [isOpen, agencyId]);
  
  const fetchUsers = async () => {
    try {
      const res = await axios.get(
        `${API}/admin/permissions/users/${agencyId}?exclude_user_id=${targetUser?.user_id || ''}`,
        { headers }
      );
      setUsers(res.data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      toast.error('Failed to load users');
    }
  };
  
  const handlePreview = async () => {
    if (!sourceUserId) {
      toast.error('Please select a source user');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/admin/permissions/copy-preview`,
        {
          source_user_id: sourceUserId,
          target_user_id: targetUser.user_id,
          agency_id: agencyId,
          copy_mode: copyMode,
          copy_scope: copyScope,
        },
        { headers }
      );
      
      setPreview(res.data);
      setShowPreview(true);
      
      if (!res.data.is_valid) {
        toast.error(res.data.validation_errors?.join(', ') || 'Preview validation failed');
      }
    } catch (err) {
      console.error('Preview error:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };
  
  const handleApply = async () => {
    if (!preview?.is_valid) {
      toast.error('Cannot apply - preview validation failed');
      return;
    }
    
    setApplying(true);
    try {
      const res = await axios.post(
        `${API}/admin/permissions/copy-apply`,
        {
          source_user_id: sourceUserId,
          target_user_id: targetUser.user_id,
          agency_id: agencyId,
          copy_mode: copyMode,
          copy_scope: copyScope,
        },
        { headers }
      );
      
      toast.success(
        `Permissions copied: ${res.data.permissions_added} added, ${res.data.permissions_removed} removed`
      );
      
      if (onSuccess) onSuccess(res.data);
      onClose();
    } catch (err) {
      console.error('Apply error:', err);
      toast.error(err.response?.data?.detail || 'Failed to apply permissions');
    } finally {
      setApplying(false);
    }
  };
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  const sourceUser = users.find(u => u.user_id === sourceUserId);
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="copy-permissions-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="text-amber-500" size={20} />
            Copy Permissions
          </DialogTitle>
          <DialogDescription>
            Copy permission configuration from another user in this account.
            All permissions are validated against membership, entitlements, and section access controls.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Target User Info */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <User className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Target User</p>
                <p className="font-medium text-stone-900">{targetUser?.name || targetUser?.email}</p>
                <Badge variant="outline" className="mt-1">{targetUser?.role}</Badge>
              </div>
            </div>
          </div>
          
          {!showPreview ? (
            <>
              {/* Source User Selection */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Copy From (Source User)
                </label>
                <Select value={sourceUserId} onValueChange={setSourceUserId}>
                  <SelectTrigger className="w-full" data-testid="source-user-select">
                    <SelectValue placeholder="Select a user to copy from..." />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user.user_id} value={user.user_id}>
                        <div className="flex items-center gap-2">
                          <span>{user.name || user.email}</span>
                          <Badge variant="outline" className="text-xs">{user.role}</Badge>
                          {user.permission_override_count > 0 && (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                              {user.permission_override_count} overrides
                            </Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Copy Mode */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Apply Mode
                </label>
                <Select value={copyMode} onValueChange={setCopyMode}>
                  <SelectTrigger className="w-full" data-testid="copy-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="replace_all">
                      <div>
                        <span className="font-medium">Replace All Permissions</span>
                        <p className="text-xs text-slate-500">Remove existing, add source permissions</p>
                      </div>
                    </SelectItem>
                    <SelectItem value="merge_missing">
                      <div>
                        <span className="font-medium">Merge Missing Only</span>
                        <p className="text-xs text-slate-500">Only add permissions target doesn't have</p>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Copy Scope */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Copy Scope
                </label>
                <Select value={copyScope} onValueChange={setCopyScope}>
                  <SelectTrigger className="w-full" data-testid="copy-scope-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="permissions_only">
                      <div>
                        <span className="font-medium">Permissions Only</span>
                        <p className="text-xs text-slate-500">Keep target's current role</p>
                      </div>
                    </SelectItem>
                    <SelectItem value="role_and_permissions">
                      <div>
                        <span className="font-medium">Role + Permissions</span>
                        <p className="text-xs text-slate-500">Also copy the source user's role</p>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Info Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <Info className="text-blue-500 mt-0.5" size={18} />
                <div className="text-sm text-blue-700">
                  <p className="font-medium">Permissions are validated against:</p>
                  <ul className="mt-1 list-disc list-inside text-blue-600 space-y-0.5">
                    <li>Agency membership status and plan entitlements</li>
                    <li>Section Access Control settings</li>
                    <li>Payment Processor Access restrictions</li>
                    <li>Role template ceiling</li>
                  </ul>
                </div>
              </div>
            </>
          ) : (
            /* Preview Results */
            <>
              {/* Source → Target */}
              <div className="flex items-center justify-between bg-slate-50 rounded-lg p-4">
                <div className="text-center">
                  <p className="text-xs text-slate-500">Source</p>
                  <p className="font-medium">{preview.source_user_name}</p>
                  <Badge variant="outline">{preview.source_role}</Badge>
                </div>
                <ArrowRight className="text-slate-400" />
                <div className="text-center">
                  <p className="text-xs text-slate-500">Target</p>
                  <p className="font-medium">{preview.target_user_name}</p>
                  <Badge variant="outline">{preview.target_role}</Badge>
                  {preview.role_will_change && (
                    <Badge className="ml-2 bg-amber-100 text-amber-700">
                      → {preview.new_role}
                    </Badge>
                  )}
                </div>
              </div>
              
              {/* Validation Errors */}
              {!preview.is_valid && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700">
                    <XCircle size={18} />
                    <span className="font-medium">Validation Failed</span>
                  </div>
                  <ul className="mt-2 list-disc list-inside text-red-600 text-sm">
                    {preview.validation_errors?.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {preview.is_valid && (
                <>
                  {/* Summary Cards */}
                  <PreviewSummary preview={preview} />
                  
                  {/* Permissions to Add */}
                  {preview.permissions_to_add?.length > 0 && (
                    <div>
                      <button 
                        className="flex items-center gap-2 text-sm font-medium text-emerald-700 mb-2 hover:text-emerald-800"
                        onClick={() => toggleSection('add')}
                      >
                        {expandedSections.add ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        <CheckCircle2 size={16} />
                        Permissions to Add ({preview.permissions_to_add.length})
                      </button>
                      {expandedSections.add && (
                        <div className="space-y-2 ml-6">
                          {preview.permissions_to_add.map(perm => (
                            <PermissionItem key={perm.entitlement_key} perm={perm} status="add" />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Permissions to Remove */}
                  {preview.permissions_to_remove?.length > 0 && (
                    <div>
                      <button 
                        className="flex items-center gap-2 text-sm font-medium text-red-700 mb-2 hover:text-red-800"
                        onClick={() => toggleSection('remove')}
                      >
                        {expandedSections.remove ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        <XCircle size={16} />
                        Permissions to Remove ({preview.permissions_to_remove.length})
                      </button>
                      {expandedSections.remove && (
                        <div className="space-y-2 ml-6">
                          {preview.permissions_to_remove.map(perm => (
                            <PermissionItem key={perm.entitlement_key} perm={perm} status="remove" />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Blocked Permissions */}
                  {preview.permissions_blocked?.length > 0 && (
                    <div>
                      <button 
                        className="flex items-center gap-2 text-sm font-medium text-amber-700 mb-2 hover:text-amber-800"
                        onClick={() => toggleSection('blocked')}
                      >
                        {expandedSections.blocked ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        <AlertTriangle size={16} />
                        Blocked by Restrictions ({preview.permissions_blocked.length})
                      </button>
                      {expandedSections.blocked && (
                        <div className="space-y-2 ml-6">
                          {preview.permissions_blocked.map(perm => (
                            <PermissionItem 
                              key={perm.entitlement_key} 
                              perm={perm} 
                              status="blocked"
                              reason={perm.reason}
                              reasonCode={perm.reason_code}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Skipped Permissions */}
                  {preview.permissions_skipped?.length > 0 && (
                    <div>
                      <button 
                        className="flex items-center gap-2 text-sm font-medium text-slate-600 mb-2 hover:text-slate-700"
                        onClick={() => toggleSection('skipped')}
                      >
                        {expandedSections.skipped ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        <Info size={16} />
                        Skipped ({preview.permissions_skipped.length})
                      </button>
                      {expandedSections.skipped && (
                        <div className="space-y-2 ml-6">
                          {preview.permissions_skipped.map(perm => (
                            <PermissionItem 
                              key={perm.entitlement_key} 
                              perm={perm} 
                              status="skipped"
                              reason={perm.reason}
                              reasonCode={perm.reason_code}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
        
        <DialogFooter className="flex justify-between">
          {showPreview ? (
            <>
              <Button variant="outline" onClick={() => setShowPreview(false)}>
                Back
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleApply}
                  disabled={applying || !preview?.is_valid}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="apply-permissions-btn"
                >
                  {applying ? (
                    <>
                      <RefreshCw className="animate-spin mr-2" size={16} />
                      Applying...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2" size={16} />
                      Apply Permissions
                    </>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div></div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button 
                  onClick={handlePreview}
                  disabled={loading || !sourceUserId}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="preview-permissions-btn"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="animate-spin mr-2" size={16} />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Eye className="mr-2" size={16} />
                      Preview Changes
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CopyPermissionsModal;
